/**
 * space_post — the sector's post chain (report §9 R1): the pieces PostModule's SPACE PROFILE block calls.
 *
 *  - `applySpacePostProfile(post)` rewrites the tier's pipeline config IN PLACE for a vacuum (the rules are
 *    `spacePostConfig` in space_look.ts — no aerial, no fog, no god rays, no AO/SSGI/SSR/DoF; bloom at 1.0,
 *    flares at 4) and appends a final IGN DITHER (Jimenez 2014, ±½ LSB) after the display transform: dark
 *    nebula and Milky Way gradients band at 8 bits. The dither rides the pipeline's last display-referred
 *    hook (`colorblind.transform`), composed AFTER any accessibility transform the tier carries.
 *  - `SpaceExposure` — the engine's histogram AutoExposureSystem under the ASYMMETRIC space clamp
 *    (−2 EV … +¼ EV, SPACE_EXPOSURE) with the sun-glare bias on its base exposure; saves and restores the
 *    renderer's exposure across the flight. Its base is THE REGIME BASE (space_look.spaceBaseExposure — the curve's
 *    reference, never the ground meter's live correction) and it takes no reading while the world transition holds
 *    the frame (session 3, 2026-09-27). The metering itself is the engine controller's (32-px regions, one-turn reads,
 *    `decodeMeterBlock` — THE READBACK and ONE-FRAME laws); this class has no readback of its own.
 *
 * Global render state flips here ONLY at activate / deactivate (PostModule rebuilds once, under the
 * launch / landing cover) — never on a system swap (SpaceModule keeps the profile across a hyperwarp).
 */
import { Fn, vec2, vec4, float, fract, dot, screenCoordinate } from 'three/tsl';
import type * as THREE from 'three/webgpu';
import { AutoExposureSystem } from 'enginex/render/post/AutoExposure.js';
import { spacePostConfig, SPACE_EXPOSURE, sunGlareBias, spaceBaseExposure, SPACE_BASE_FALLBACK, spaceEyeState } from './space_look.js';
import { exposureState } from '../photometry.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** What the last profile application did (telemetry for `__spaceLook()`). */
export const spacePostState = { applied: false, dropped: [] as string[], bloom: null as unknown, flares: null as unknown, dither: false };

/** The final ±½-LSB interleaved-gradient-noise dither on the display-referred output (alpha untouched). */
export function spaceDither(prev?: (rgb: unknown) => unknown): (rgb: unknown) => unknown {
  return (input: unknown): unknown => {
    const src = (prev ? prev(input) : input) as N;
    return Fn(() => {
      const c = vec4(src);
      const ign = fract(float(52.9829189).mul(fract(dot(screenCoordinate.xy, vec2(0.06711056, 0.00583715)))));
      return vec4(c.rgb.add(ign.sub(0.5).mul(1 / 255)), c.a);
    })();
  };
}

/** Rewrite the tier's post config for the sector, in place. Returns the dropped passes. */
export function applySpacePostProfile(post: Record<string, unknown>): string[] {
  const { post: next, dropped } = spacePostConfig(post);
  for (const k of Object.keys(post)) if (!(k in next)) delete post[k];
  Object.assign(post, next);
  const cb = post.colorblind as { transform?: (rgb: unknown) => unknown; enabled?: boolean } | undefined;
  const prev = cb?.transform && cb.enabled !== false ? cb.transform : undefined;
  post.colorblind = { transform: spaceDither(prev), enabled: true };
  spacePostState.applied = true;
  spacePostState.dropped = dropped;
  spacePostState.bloom = post.bloom ?? null;
  spacePostState.flares = post.lensFlares ?? null;
  spacePostState.dither = true;
  return dropped;
}

/** The sun-glare inputs SpaceModule refreshes every frame (visible fraction, screen distance, on screen). */
export const spaceGlare = { visible: 0, ndcDist: 2, onScreen: false };

/** The sector's eye: the engine's metering + adaptation under the space clamp and the glare bias. */
export class SpaceExposure {
  private sys: AutoExposureSystem | null = null;
  private saved: number | null = null;
  private base = SPACE_BASE_FALLBACK;
  private wasHeld = false;
  /** Last applied values (telemetry): `base` the regime base, `held` the transition hold, `fresh` eyes restarted at a hold's end. */
  readonly stats = { exposure: 0, adaptation: 1, target: 1, bias: 1, base: 0, held: false, fresh: 0 };

  tick(dt: number, renderer: THREE.WebGPURenderer, getRT: () => { width: number; height: number } | null, world: unknown, held = false): void {
    if (this.saved === null) {
      this.saved = renderer.toneMappingExposure;
      // THE REGIME BASE (session 3): the curve's reference × the player's compensation — the renderer's LIVE exposure
      // carried the ground meter's correction at launch (up to +1.5 EV) into the whole flight.
      this.base = spaceBaseExposure(exposureState);
    }
    const bias = sunGlareBias(spaceGlare.visible, spaceGlare.ndcDist, spaceGlare.onScreen);
    this.stats.base = this.base;
    this.stats.bias = bias;
    this.stats.held = held;
    // THE BOOT READING in the sector (the ground eye's law, lighting 2026-09-26): under the launch / landing cover the
    // renderer is PAUSED — nothing draws into the scene target and a readback there is a GPU submit the hold exists to
    // stop; the first frames after it are half-built. No reading while held (the eye rests neutral); a FRESH eye when
    // the hold lifts.
    if (held) {
      this.wasHeld = true;
      renderer.toneMappingExposure = this.base * bias;
      this.stats.exposure = renderer.toneMappingExposure;
      this.stats.adaptation = 1;
      this.stats.target = 1;
      return;
    }
    if (this.wasHeld) { this.wasHeld = false; this.sys = null; this.stats.fresh++; }
    if (!this.sys) {
      this.sys = new AutoExposureSystem({
        renderer,
        getRenderTarget: getRT,
        config: {
          key: SPACE_EXPOSURE.key, minExposure: SPACE_EXPOSURE.minExposure, maxExposure: SPACE_EXPOSURE.maxExposure,
          metering: SPACE_EXPOSURE.metering, lowPercentile: SPACE_EXPOSURE.lowPercentile, highPercentile: SPACE_EXPOSURE.highPercentile,
          speedUp: SPACE_EXPOSURE.speedUp, speedDown: SPACE_EXPOSURE.speedDown, intervalFrames: SPACE_EXPOSURE.intervalFrames,
          baseExposure: this.base,
        },
      });
    }
    this.sys.controller.config.baseExposure = this.base * bias;
    this.sys.execute(dt, world as never);
    this.stats.exposure = renderer.toneMappingExposure;
    spaceEyeState.exposure = this.stats.exposure; spaceEyeState.base = this.base; // sky/post (2026-09-27): the corona follows the eye
    this.stats.adaptation = this.sys.controller.adaptation;
    this.stats.target = this.sys.controller.target;
  }

  /** Leave space: the renderer's exposure goes back to what it was at launch. */
  stop(renderer: THREE.WebGPURenderer): void {
    if (this.saved !== null) renderer.toneMappingExposure = this.saved;
    spaceEyeState.exposure = 0; spaceEyeState.base = 0;
    this.sys = null;
    this.saved = null;
    this.wasHeld = false;
  }

  /** The eye holds the renderer's exposure (from the first flight tick — held or not — until `stop`). */
  get running(): boolean { return this.saved !== null; }
}
